import { Linkedin, Mail, Facebook, ArrowRight, TrendingUp } from 'lucide-react';
import { FaInstagram } from 'react-icons/fa';

export default function Home({ darkMode, scrollToSection }) {
  const stats = [
    { value: '10+', label: 'Years Experience' },
    { value: '500+', label: 'Clients Served' },
    { value: '3', label: 'Top Insurers' },
  ];

  return (
    <section id="home" className={`min-h-screen pt-20 lg:pt-0 flex items-center relative overflow-hidden
      ${darkMode ? 'bg-[#0A0F1E]' : 'bg-[#F8F6F1]'}`}>

      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute top-20 right-10 w-72 h-72 rounded-full blur-3xl opacity-10
          ${darkMode ? 'bg-[#C9A84C]' : 'bg-[#C9A84C]'}`}></div>
        <div className={`absolute bottom-20 left-20 w-96 h-96 rounded-full blur-3xl opacity-5
          ${darkMode ? 'bg-blue-500' : 'bg-blue-300'}`}></div>
        {/* Grid pattern */}
        <div className={`absolute inset-0 opacity-[0.02] ${darkMode ? 'block' : 'hidden'}`}
          style={{
            backgroundImage: `linear-gradient(#C9A84C 1px, transparent 1px), linear-gradient(90deg, #C9A84C 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}>
        </div>
      </div>

      <div className="container mx-auto px-4 lg:px-16 py-16 relative z-10">
        <div className="grid lg:grid-cols-5 gap-12 items-center">

          {/* Left Content */}
          <div className="lg:col-span-3 order-2 lg:order-1">
            {/* Badge */}
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium tracking-widest uppercase mb-8 border
              ${darkMode
                ? 'border-[#C9A84C]/30 text-[#C9A84C] bg-[#C9A84C]/5'
                : 'border-[#C9A84C]/40 text-[#8B6914] bg-[#C9A84C]/10'
              }`}>
              <TrendingUp size={12} />
              Trusted Financial Advisory
            </div>

            <h1 className={`text-5xl lg:text-7xl font-bold mb-4 leading-none tracking-tight
              ${darkMode ? 'text-white' : 'text-[#0A0F1E]'}`}>
              Rise<span className="text-[#C9A84C]">Cap</span>
            </h1>

            <p className={`text-lg lg:text-xl mb-8 max-w-xl leading-relaxed font-light
              ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              A trusted financial advisor and consultant with years of experience helping individuals and families secure their financial future through smart planning and trusted solutions.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-14">
              <button
                onClick={() => scrollToSection('services')}
                className="group flex items-center gap-2 px-7 py-3.5 rounded-xl font-medium text-sm tracking-wide transition-all duration-300
                  bg-[#C9A84C] text-[#0A0F1E] hover:bg-[#E2C06A] shadow-lg shadow-[#C9A84C]/20 hover:shadow-[#C9A84C]/40"
              >
                Explore Services
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className={`px-7 py-3.5 rounded-xl font-medium text-sm tracking-wide transition-all duration-300 border
                  ${darkMode
                    ? 'border-[#C9A84C]/30 text-[#C9A84C] hover:bg-[#C9A84C]/10 hover:border-[#C9A84C]'
                    : 'border-[#C9A84C]/40 text-[#8B6914] hover:bg-[#C9A84C]/10 hover:border-[#C9A84C]'
                  }`}
              >
                Get In Touch
              </button>
            </div>

            {/* Stats Row */}
            <div className={`flex gap-8 pb-10 mb-10 border-b ${darkMode ? 'border-white/5' : 'border-gray-200'}`}>
              {stats.map((stat, i) => (
                <div key={i}>
                  <p className="text-2xl font-bold text-[#C9A84C]">{stat.value}</p>
                  <p className={`text-xs mt-0.5 tracking-wide ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div>
              <p className={`mb-4 text-xs uppercase tracking-widest font-medium ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                Connect With Me
              </p>
              <div className="flex space-x-4">
                {[
                  { href: 'https://www.linkedin.com/in/manojpant991123/', icon: <Linkedin size={18} /> },
                  { href: 'mailto:risecap212@gmail.com', icon: <Mail size={18} /> },
                  { href: 'https://www.instagram.com/risecap_finance/?hl=en', icon: <FaInstagram size={18} /> },
                  { href: 'https://www.facebook.com/profile.php?id=100093338240391', icon: <Facebook size={18} /> },
                ].map((social, i) => (
                  <a key={i} href={social.href}
                    className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-all duration-300
                      ${darkMode
                        ? 'border-[#C9A84C]/20 text-gray-500 hover:border-[#C9A84C] hover:text-[#C9A84C] hover:bg-[#C9A84C]/10'
                        : 'border-[#C9A84C]/30 text-gray-400 hover:border-[#C9A84C] hover:text-[#8B6914] hover:bg-[#C9A84C]/10'
                      }`}>
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right — Profile */}
          <div className="lg:col-span-2 order-1 lg:order-2 flex justify-center">
            <div className="relative">
              {/* Gold ring decoration */}
              <div className="absolute -inset-4 rounded-3xl border border-[#C9A84C]/20 rotate-3"></div>
              <div className="absolute -inset-8 rounded-3xl border border-[#C9A84C]/10 -rotate-2"></div>

              {/* Profile image */}
              <div className={`relative rounded-3xl overflow-hidden w-64 h-72 lg:w-72 lg:h-80 border-2
                ${darkMode ? 'border-[#C9A84C]/30' : 'border-[#C9A84C]/40'}`}>
                <img
                  src="/images/profile/profile-placeholder.jpg"
                  alt="Manoj Pant"
                  className="w-full h-full object-cover"
                />
                {/* Gradient overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0A0F1E]/80 to-transparent flex items-end p-4">
                  <div>
                    <p className="text-white font-semibold text-lg leading-tight">Manoj Pant</p>
                    <p className="text-[#C9A84C] text-xs tracking-wide">Financial Advisor & Consultant</p>
                  </div>
                </div>
              </div>

              {/* Floating badge */}
              <div className={`absolute -bottom-4 -right-4 px-4 py-2.5 rounded-xl shadow-xl border text-center
                ${darkMode ? 'bg-[#080D1A] border-[#C9A84C]/20' : 'bg-white border-[#C9A84C]/30'}`}>
                <p className="text-[#C9A84C] text-xl font-bold leading-none">10+</p>
                <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Yrs Exp.</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}